import type { OpenCall, OpenCallTag } from "../types";
import { getOpenCallTag } from "./getOpenCallTag";

type CategorizedOpenCalls = Record<OpenCallTag | "Ongoing", OpenCall[]>;

const accumulator: CategorizedOpenCalls = {
  "Coming Soon": [],
  "Submissions Open": [],
  Ongoing: [],
  "Voting Soon": [],
  "Voting Open": [],
  "Under Curation": [],
  "Artists Selected": [],
  "On Display": [],
  Concluded: [],
};

const categorizeOpenCalls = (
  openCalls: OpenCall[],
  excludeById?: string
): CategorizedOpenCalls => {
  if (openCalls?.length === 0) return accumulator;

  const categorized = openCalls.reduce<CategorizedOpenCalls>((acc, current) => {
    // Skip excluded OpenCall, e.g. if we're looking at it on details page
    if (excludeById === current.id) return acc;

    const tag = getOpenCallTag(current);

    if (tag === "Submissions Open") {
      if (!current.submissionEndDate) {
        // open but ongoing
        return { ...acc, Ongoing: [...acc.Ongoing, current] };
      }
      return {
        ...acc,
        "Submissions Open": [...acc["Submissions Open"], current],
      };
    }

    return { ...acc, [tag]: [...acc[tag], current] };
  }, accumulator);

  return categorized;
};

const sortSubmissionEnd = (a: OpenCall, b: OpenCall) =>
  a.submissionEndDate?.localeCompare(b?.submissionEndDate ?? "") ?? 0;

const sortSubmissionStart = (a: OpenCall, b: OpenCall) =>
  a.submissionStartDate?.localeCompare(b?.submissionStartDate ?? "") ?? 0;

const sortVotingEnd = (a: OpenCall, b: OpenCall) =>
  a.communityVotingEndDate?.localeCompare(b?.communityVotingEndDate ?? "") ?? 0;

const sortVotingStart = (a: OpenCall, b: OpenCall) =>
  a.communityVotingStartDate?.localeCompare(
    b?.communityVotingStartDate ?? ""
  ) ?? 0;

const sortOpenCalls = (categorized: CategorizedOpenCalls) => ({
  open: [
    ...categorized["Submissions Open"].sort(sortSubmissionEnd),
    ...categorized.Ongoing.sort(sortSubmissionStart),
    ...categorized["Coming Soon"].sort(sortSubmissionStart),
  ],
  voting: [
    ...categorized["Voting Open"].sort(sortVotingEnd),
    ...categorized["Voting Soon"].sort(sortVotingStart),
  ],
  closed: [
    ...categorized["Under Curation"].sort(sortSubmissionEnd).reverse(),
    ...categorized["Artists Selected"].sort(sortSubmissionEnd).reverse(),
    ...categorized["On Display"].sort(sortSubmissionEnd).reverse(),
    ...categorized.Concluded.sort(sortSubmissionEnd).reverse(),
  ],
});

export { categorizeOpenCalls, sortOpenCalls };
